// The add-a-line form under the parts list. Built for rapid entry: pick a
// target, type the number, press Enter, and focus is back on the picker for the
// next line. Part families and specific parts are two ways to name a target,
// not two forms.

import React, { useEffect, useRef, useState } from 'react';
import { buttonClass } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import Panel from '../components/ui/Panel';
import { useApi } from '../apiHelpers';
import EntityCombobox, { type ComboboxOption } from '../components/EntityCombobox';
import ItemPicker from '../components/ItemPicker';
import { laneLabel } from '../components/review/fieldStyles';
import { ChipToggle } from '../components/ui/ChipToggle';
import { Field } from '../components/ui/Field';
import { SegTrack } from '../components/ui/SegTrack';
import type { PartFamilyRow, PartsCatalogRow } from '../Receiving/types';
import { bomErrorMessage, type NewLineInput } from './bomsApi';
import { VariantControl } from './BomLines';

type Mode = 'family' | 'part';

const FAMILY_PICKER_ID = 'bom-add-family';
const PART_PICKER_ID = 'bom-add-part';

const BomAddLine: React.FC<{ onAdd: (input: NewLineInput) => Promise<void> }> = ({ onAdd }) => {
  const api = useApi();
  const [families, setFamilies] = useState<PartFamilyRow[]>([]);
  const [familiesFailed, setFamiliesFailed] = useState(false);
  const [mode, setMode] = useState<Mode>('family');
  const [family, setFamily] = useState<PartFamilyRow | null>(null);
  const [part, setPart] = useState<PartsCatalogRow | null>(null);
  const [qty, setQty] = useState('1');
  const [traced, setTraced] = useState(true);
  const [variant, setVariant] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Bumping this remounts the pickers (clearing their typed text) and, through
  // the effect below, puts focus back on the target field.
  const [resetKey, setResetKey] = useState(0);
  const qtyRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const rows = (await api.get('/part-families')) as PartFamilyRow[];
        if (live) setFamilies((rows || []).filter((f) => f.is_active));
      } catch {
        if (live) setFamiliesFailed(true);
      }
    })();
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (resetKey === 0) return;
    document.getElementById(mode === 'family' ? FAMILY_PICKER_ID : PART_PICKER_ID)?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  const toQty = () => {
    qtyRef.current?.focus();
    qtyRef.current?.select();
  };

  const switchMode = (next: Mode) => {
    if (next === mode) return;
    setMode(next);
    setFamily(null);
    setPart(null);
    // A family line is scanned at the bench by default; a single part is not.
    setTraced(next === 'family');
    setError(null);
    setResetKey((k) => k + 1);
  };

  const submit = async () => {
    if (busy) return;
    const target = mode === 'family' ? family?.id : part?.id;
    if (target === undefined || target === null) {
      setError('Pick a part family or a specific part first.');
      return;
    }
    const n = Number(qty);
    if (qty.trim() === '' || !Number.isInteger(n) || n <= 0) {
      setError('Per unit takes whole numbers above zero.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onAdd({
        ...(mode === 'family' ? { part_family_id: target } : { part_catalog_id: target }),
        quantity_per: n,
        traced,
        variant,
      });
      setFamily(null);
      setPart(null);
      setQty('1');
      setTraced(mode === 'family');
      setVariant(null);
      setResetKey((k) => k + 1);
    } catch (err) {
      setError(bomErrorMessage(err, 'The line was not added. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  const familyOptions: ComboboxOption[] = families.map((f) => ({
    id: f.id,
    label: f.label,
    sublabel: f.key,
    data: f,
  }));

  return (
    <Panel label="Add to the parts list">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <h2 className="text-sm font-semibold text-on-surface">Add to the list</h2>
        <SegTrack
          label="What to add"
          size="sm"
          value={mode}
          onChange={(key) => switchMode(key as Mode)}
          options={[
            { key: 'family', label: 'Part family' },
            { key: 'part', label: 'Specific part' },
          ]}
        />
      </div>

      <div
        className="flex flex-wrap items-end gap-3"
        onKeyDownCapture={(e) => {
          // The variant selector's closed trigger: Enter adds the line, as the native select did.
          const t = e.target;
          if (e.key === 'Enter' && t instanceof HTMLElement && t.getAttribute('aria-haspopup') === 'listbox' && t.getAttribute('aria-expanded') === 'false') {
            e.preventDefault();
            e.stopPropagation();
            submit();
          }
        }}
      >
        <div className="min-w-[16rem] flex-1">
          {mode === 'family' ? (
            <EntityCombobox
              key={`family-${resetKey}`}
              id={FAMILY_PICKER_ID}
              label="Part family"
              labelClassName={laneLabel}
              placeholder="Search part families"
              options={familyOptions}
              value={family ? family.id : ''}
              onSelect={(o) => {
                setFamily((o?.data as PartFamilyRow) || null);
                if (o) setTimeout(toQty, 0);
              }}
              noOptionsMessage={
                familiesFailed
                  ? 'Part families did not load. Reload the page to try again.'
                  : 'No part families yet.'
              }
              emptyMessage="No part family matches that."
            />
          ) : (
            <div>
              <label htmlFor={PART_PICKER_ID} className={laneLabel}>
                Specific part
              </label>
              <ItemPicker
                key={`part-${resetKey}`}
                id={PART_PICKER_ID}
                value={part}
                onSelect={(row) => {
                  setPart(row);
                  if (row) setTimeout(toQty, 0);
                }}
              />
            </div>
          )}
        </div>

        <div>
          <label htmlFor="bom-add-qty" className={laneLabel}>
            Per unit
          </label>
          <Field
            thumb
            ref={qtyRef}
            id="bom-add-qty"
            type="number"
            inputMode="numeric"
            step="1"
            min="1"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                submit();
              }
            }}
            className="w-24 text-right"
          />
        </div>

        <div>
          <span className={laneLabel}>Scanned</span>
          <ChipToggle checked={traced} onChange={() => setTraced((t) => !t)}>
            {traced ? 'Scanned at the bench' : 'Not scanned'}
          </ChipToggle>
        </div>

        <div>
          <span className={laneLabel}>Variant</span>
          <VariantControl
            value={variant}
            onCommit={setVariant}
            ariaLabel="Variant for the new line"
          />
        </div>

        <button
          type="button"
          onClick={submit}
          disabled={busy}
          className={buttonClass('filled')}
        >
          {busy ? 'Adding…' : 'Add line'}
        </button>
      </div>

      <Alert show={!!error} variant="text" className="mt-3">
        Not added: {error}
      </Alert>
    </Panel>
  );
};

export default BomAddLine;
